import { useCallback, useState } from 'react';

import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { ImageProperties, Props } from './image.constants';
import { ImageViewFill, ImageViewFit } from './image-view';
import ImageCreate from './image-create';
import ImageCrop from './image-crop';
import { ComponentActions } from '../components.style';
import { ImageContainer } from './image.style';
import { getBaseImagePayload, hasImage, hasStimulusImage } from './image.utils';
import { ComponentCaptionEdit } from '../shared/component-caption/component-caption-edit';
import { ComponentDimensions } from '../shared/component-height/component-dimensions';
import StimulusUsageMessage from '../shared/stimulus-message';
import ComponentAltTextButton from '../shared/component-alt-text/component-alt-text-button';
import ComponentImageModal from '../shared/component-image-modal/component-image-modal';
import { DRAFT_ACTION_COMPONENT_IMAGE_UPDATE } from 'global/constants';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';

const ImageEdit = (props: Props & { contentId: number }) => {
  const { sectionId, contentId, id, properties, stimulusResourceProperties } =
    props;

  const hasAStimulusImage = hasStimulusImage({
    ...props,
    __typename: 'ActivitiesImageComponent',
  });
  const hasAnImage = hasImage({
    ...props,
    __typename: 'ActivitiesImageComponent',
  });

  const [elementRef, setElementRef] = useState<HTMLDivElement | null>(null);

  const { croppingComponentId, activeComponent, setCroppingComponentId } =
    useActivityBuilderContext();

  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const { updateComponentProperties, loading } =
    useBatchComponentUpdateContext();

  const handleUpdateHeight = useCallback(
    (width: number, height: number) => {
      const dimensionData = {
        ...properties.dimensionData,
        width,
        height: Math.floor(height),
      };
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: {
              ...getBaseImagePayload(properties),
              dimensionData,
              cropData: properties.cropData
                ? {
                    width,
                    height,
                    naturalWidth: properties.cropData.naturalWidth,
                    naturalHeight: properties.cropData.naturalHeight,
                    x: properties.cropData.x,
                    y: properties.cropData.y,
                    zoom: properties.cropData.zoom,
                  }
                : null,
            },
          },
        },
        properties: {
          ...properties,
          dimensionData,
        },
        typename: 'ActivitiesImageComponent',
      });
    },
    [id, sectionId, properties, updateComponentProperties]
  );

  const handleUpdate = useCallback(
    (properties: ImageProperties) => {
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: getBaseImagePayload(properties),
          },
        },
        properties,
        typename: 'ActivitiesImageComponent',
      });
    },
    [id, sectionId, updateComponentProperties]
  );

  const imageUrls = properties.imageUrls;
  const isFit = !properties.cropData;

  if (croppingComponentId === id) {
    return (
      <ImageCrop
        {...props}
        canFlip={!hasAStimulusImage}
        onCropComplete={() => setCroppingComponentId('')}
        onCaptionChange={caption => handleUpdate({ ...properties, caption })}
      />
    );
  }

  return (
    <ComponentDimensions
      id={id}
      showHandle={activeComponent?.id === id}
      defaultValue={properties.dimensionData}
      onResizeEnd={handleUpdateHeight}
    >
      {isVisible && (
        <ComponentImageModal
          extraContent={
            hasAStimulusImage ? (
              <StimulusUsageMessage
                componentId={id}
                stimulusId={properties.stimulusId!}
                contentId={contentId}
                sectionId={sectionId}
              />
            ) : null
          }
          loading={loading}
          containerDimensions={{
            width: elementRef?.offsetWidth || properties.dimensionData.width,
            height: elementRef?.offsetHeight || properties.dimensionData.height,
          }}
          canFlip={!hasAStimulusImage}
          canUpdateAltText={!hasAStimulusImage}
          canDelete={!hasAStimulusImage}
          canAddSource={!hasAStimulusImage}
          image={
            hasAStimulusImage
              ? stimulusResourceProperties?.image
              : properties.image
          }
          sourceUrl={properties.sourceUrl}
          altText={
            hasAStimulusImage
              ? stimulusResourceProperties?.altText
              : properties.altText
          }
          cropData={properties.cropData}
          closeModal={closeModal}
          onUpdateData={data => {
            updateComponentProperties({
              input: {
                sectionId,
                componentId: id,
                componentData: {
                  properties: {
                    sourceUrl: data.sourceUrl,
                    ...getBaseImagePayload({
                      ...properties,
                      cropData: data.cropData
                        ? {
                            ...data.cropData,
                            __typename: 'CropData',
                          }
                        : null,
                    }),
                    altText: data.altText,
                    image: data.image
                      ? {
                          fileName: data.image.fileName,
                          key: data.image.key,
                          imageType: 'content',
                          data: {
                            flip: data.image.data?.flip || null,
                          },
                        }
                      : null,
                  },
                },
                addDraftAction: DRAFT_ACTION_COMPONENT_IMAGE_UPDATE,
              },
              typename: 'ActivitiesImageComponent',
              isSaveImmediate: true,
              onCompleted: () => {
                closeModal();
              },
            });
          }}
        />
      )}
      {hasAnImage ? (
        <>
          <ImageContainer
            ref={setElementRef}
            $fullBorderRadius={!properties.showCaptionOnLesson}
          >
            {isFit ? (
              <ImageViewFit imageUrls={imageUrls || []} />
            ) : (
              <ImageViewFill imageUrls={imageUrls || []} />
            )}
          </ImageContainer>

          {properties.showCaptionOnLesson && (
            <ComponentCaptionEdit
              caption={properties.caption}
              onChange={caption => handleUpdate({ ...properties, caption })}
            />
          )}

          <ComponentActions>
            <ComponentAltTextButton
              hasAltText={
                !!(hasAStimulusImage && stimulusResourceProperties?.altText
                  ? stimulusResourceProperties.altText
                  : properties.altText)
              }
              onClick={openModal}
            />
          </ComponentActions>
        </>
      ) : (
        <ImageCreate {...props} />
      )}
    </ComponentDimensions>
  );
};

export default ImageEdit;
